<script setup>
import { computed } from "vue";
import { useAchievementsStore } from "@stores/achievements";
import IconAchivement from "@components/icons/Achivement.vue";

const props = defineProps({
    slug: { type: String, required: true },
});

const store = useAchievementsStore();
const definition = computed(() => store.bySlug(props.slug));
const progress = computed(() => store.currentProgress(props.slug));

const tierStates = computed(() => {
    if (!definition.value || !progress.value) return [];
    const count = progress.value.rawCount;
    return definition.value.levels.map((level) => {
        if (count >= level.target_end) return "complete";
        if (count >= level.target_start) return "active";
        return "locked";
    });
});

const totalMax = computed(() => {
    if (!definition.value) return 0;
    const levels = definition.value.levels;
    return levels[levels.length - 1].target_end;
});

const displayCount = computed(() => {
    if (!progress.value) return 0;
    return Math.min(totalMax.value, progress.value.rawCount);
});

const TIER_COLORS = [
    "text-marine-50",
    "text-yellow-400",
    "text-temperature-hot",
];

const isSingleTier = computed(() => definition.value?.levels.length === 1);

const tierColorClass = computed(() => {
    if (isSingleTier.value) return "text-temperature-hot";
    if (!progress.value) return TIER_COLORS[0];
    return TIER_COLORS[progress.value.levelIndex] ?? TIER_COLORS[0];
});

function segmentClass(state) {
    if (state === "complete") return "bg-yellow-400";
    if (state === "active") return "bg-marine-50";
    return "bg-marine-400";
}
</script>

<template>
    <div
        v-if="definition && progress"
        class="border-marine-500 relative border-b px-6 pb-6"
    >
        <div
            v-if="progress.isComplete"
            class="body-xs text-marine-800 absolute top-0 right-6 z-10 bg-yellow-400 px-2 py-1 font-bold tracking-[-0.2px] uppercase"
        >
            Complete
        </div>
        <div
            class="flex flex-col gap-4"
            :class="{ 'opacity-50': progress.isComplete }"
        >
            <div class="flex items-center gap-4">
                <div
                    class="relative isolate flex size-[72px] shrink-0 items-center justify-center"
                >
                    <IconAchivement :class="tierColorClass" />
                    <i
                        v-if="definition.icon"
                        :class="[
                            definition.icon,
                            'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-3xl text-[24px] text-yellow-400',
                        ]"
                    ></i>
                </div>
                <div
                    class="flex min-w-0 flex-1 flex-col justify-center gap-2 pr-4"
                >
                    <p class="body-md leading-[0.9] font-bold uppercase">
                        {{ definition.name }}
                    </p>
                    <p class="body-sm leading-[1.2] faded-text">
                        {{ progress.level.name }}
                    </p>
                </div>
            </div>

            <div v-if="!isSingleTier" class="flex flex-col gap-3">
                <div
                    class="flex h-[4.015px] w-full items-center justify-center"
                >
                    <div
                        class="flex h-[6px] w-full transform-[skewX(-48deg)_scaleY(0.67)] items-center gap-1"
                    >
                        <div
                            v-for="(state, idx) in tierStates"
                            :key="idx"
                            :class="[
                                'h-full min-w-px flex-1 transition-colors duration-300',
                                segmentClass(state),
                            ]"
                        ></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
